import { TAUX_LEGAUX_ACTUELS } from './tva';
import { auCentime } from './vat';
import { today, uid } from './utils';

/** Libellés des moyens de paiement, et de remboursement pour un avoir. */
export const MOYENS = {
  especes: 'Espèces',
  carte: 'Carte',
  twint: 'TWINT',
  bon: "Bon d'achat",
  autre: 'Autre',
};

/** Vrai pour un avoir : une ligne qui en rembourse une autre, ou un montant rendu. */
export function estAvoir(vente) {
  if (!vente) return false;
  return vente.ticketOrigineId != null || +vente.total < 0;
}

/**
 * Clôture d'une journée de caisse.
 *
 * <p>Les prix du catalogue sont hors taxe : une vente est enregistrée pour son
 * hors taxe, et la TVA s'ajoute. Chaque ligne porte le taux qui lui a été
 * appliqué, figé au moment de la vente — une vente est une pièce comptable, et
 * le rapport Z de l'an dernier doit montrer le taux d'alors, même si la société
 * en change depuis.
 *
 * <p>Le taux unique de la société ne suffit pas : un magasin d'alimentation
 * vend une caisse d'eau à 2,6 % et un ustensile à 8,1 % dans la même journée.
 * L'AFC attend la ventilation par taux, non une somme globale.
 *
 * <p>L'assiette exonérée y figure aussi : un livre vendu à 0 % est une recette
 * que le décompte déclare, même sans taxe. La ventilation la taisait, et le
 * sous-total hors taxe ne se retrouvait plus dans la somme des taux.
 *
 * <p>Un avoir est une ligne négative : il se retranche de l'assiette de son
 * taux et du moyen par lequel il a été remboursé, et le rapport le compte à
 * part, pour que la caisse explique ce qu'elle a rendu.
 *
 * @param {Array} ventes lignes de vente de la journée
 * @param {number} [tauxDefaut] taux à retenir pour une ligne sans taux propre
 * @returns {{ht: number, tva: number, ttc: number, ventilation: Array,
 *            tickets: number, articles: number, parPaiement: Record<string, number>,
 *            avoirs: {nombre: number, ht: number, tva: number, ttc: number}}}
 *          les totaux de la journée, ventilés
 */
export function clotureCaisse(ventes, tauxDefaut) {
  const liste = ventes || [];
  const defaut = tauxDefaut == null ? TAUX_LEGAUX_ACTUELS.normal : +tauxDefaut || 0;

  const parTaux = new Map();
  const parPaiement = {};
  let htBrut = 0;
  let articles = 0;
  const avoirs = { ht: 0, tva: 0 };
  const ticketsVente = new Set();
  const ticketsAvoir = new Set();

  for (const vente of liste) {
    const montant = +vente.total || 0;
    const taux = vente.tx != null ? +vente.tx : defaut;
    const cumul = parTaux.get(taux) || { ht: 0, tva: 0 };
    cumul.ht += montant;
    cumul.tva += (montant * taux) / 100;
    parTaux.set(taux, cumul);

    htBrut += montant;
    articles += +vente.q || 0;

    const moyen = vente.pay || 'autre';
    parPaiement[moyen] = (parPaiement[moyen] || 0) + montant * (1 + taux / 100);

    if (estAvoir(vente)) {
      avoirs.ht += montant;
      avoirs.tva += (montant * taux) / 100;
      ticketsAvoir.add(vente.ticket || vente.id);
    } else {
      ticketsVente.add(vente.ticket || vente.id);
    }
  }

  const ventilation = [...parTaux.entries()]
    .filter(([taux, cumul]) => taux > 0 || cumul.ht !== 0)
    .sort((a, b) => b[0] - a[0])
    .map(([taux, cumul]) => ({
      taux,
      ht: auCentime(cumul.ht),
      tva: auCentime(cumul.tva),
      ...(taux > 0 ? {} : { exonere: true }),
    }));

  const ht = auCentime(htBrut);
  // Sommer des montants deja arrondis reintroduit le bruit du flottant : on
  // arrondit la somme, comme le fait un document commercial.
  const tva = auCentime(ventilation.reduce((s, v) => s + v.tva, 0));

  for (const moyen of Object.keys(parPaiement)) {
    parPaiement[moyen] = auCentime(parPaiement[moyen]);
  }

  const avoirsHt = auCentime(avoirs.ht);
  const avoirsTva = auCentime(avoirs.tva);

  return {
    ht,
    tva,
    ttc: auCentime(ht + tva),
    ventilation,
    // Le ticket rassemble les lignes d'un même passage en caisse. Sans lui, la
    // clôture comptait une ligne pour un ticket : trois clients servis là où un
    // seul avait acheté trois articles. Les avoirs se comptent à part.
    tickets: ticketsVente.size,
    articles,
    parPaiement,
    avoirs: {
      nombre: ticketsAvoir.size,
      ht: avoirsHt,
      tva: avoirsTva,
      ttc: auCentime(avoirsHt + avoirsTva),
    },
  };
}

/**
 * Libellé d'une ligne de ventilation, tel qu'il s'imprime.
 *
 * @param {{taux: number, exonere?: boolean}} ligne ligne de la ventilation
 * @returns {string} « TVA 8,1 % », ou « 0 % — exonéré »
 */
export function libelleTaux(ligne) {
  const taux = String(ligne.taux).replace('.', ',') + ' %';
  return ligne.taux > 0 ? 'TVA ' + taux : taux + ' — exonéré';
}

/**
 * Numéro d'un ticket de caisse, imprimé sur le ticket et retenu sur ses lignes.
 *
 * <p>Le ticket imprimé portait un numéro que ses lignes ne connaissaient pas :
 * on ne le retrouvait plus dans l'historique, et un avoir n'avait rien à citer.
 *
 * @param {string} [prefixe] « T » pour une vente, « A » pour un avoir
 * @returns {string} un numéro daté, unique pour ce poste
 */
export function numeroTicket(prefixe = 'T') {
  return prefixe + '-' + today().replace(/\./g, '') + '-' + uid().slice(-6).toUpperCase();
}

/**
 * Lignes d'un ticket avec ce qui en reste à rendre.
 *
 * <p>Un avoir rembourse une ligne du ticket d'origine ; les avoirs déjà faits
 * s'en retranchent, pour qu'on ne rende jamais plus que ce qui a été vendu.
 *
 * @param {Array} lignes lignes de vente du ticket d'origine
 * @param {Array} ventes toutes les ventes connues, avoirs compris
 * @returns {Array<{ligne: object, vendue: number, rendue: number, restante: number}>}
 *          chaque ligne de vente avec les quantités vendue, déjà rendue et restante
 */
export function quantitesRestantes(lignes, ventes) {
  const toutes = ventes || [];
  return (lignes || [])
    .filter((l) => !estAvoir(l))
    .map((ligne) => {
      const vendue = +ligne.q || 0;
      const rendue = toutes
        .filter((v) => v.ticketOrigineId != null && String(v.ticketOrigineId) === String(ligne.id))
        .reduce((s, v) => s + Math.abs(+v.q || 0), 0);
      return { ligne, vendue, rendue, restante: Math.max(0, vendue - rendue) };
    });
}

/**
 * Lignes d'avoir à enregistrer pour un retour.
 *
 * <p>Le montant rendu est proportionnel à la quantité rendue : une remise
 * accordée à la vente reste acquise, elle ne se rembourse pas plus qu'elle n'a
 * été payée. Chaque ligne garde le taux de sa ligne d'origine, pour que le Z
 * la retranche de la bonne assiette.
 *
 * @param {object} options le retour
 * @param {Array<{ligne: object, restante: number}>} options.restantes lignes du ticket avec leur reste
 * @param {Record<string, number>} options.retours quantité rendue par identifiant de ligne
 * @param {string} options.motif raison du retour
 * @param {string} options.mode moyen de remboursement : especes, carte ou bon
 * @param {string} [options.numero] numéro du ticket d'avoir
 * @param {string} [options.date] date de l'avoir
 * @returns {Array} les lignes négatives à enregistrer ; vide si rien n'est rendu
 * @throws {Error} si une quantité dépasse ce qui reste de la ligne
 */
export function lignesAvoir({ restantes, retours, motif, mode, numero, date }) {
  const ticket = numero || numeroTicket('A');
  const jour = date || today();
  const lignes = [];
  for (const { ligne, restante } of restantes || []) {
    const q = +(retours || {})[ligne.id] || 0;
    if (q <= 0) continue;
    if (q > restante) {
      throw new Error(
        'Il ne reste que ' + restante + ' ' + (ligne.u || '') + ' à rendre sur ' + ligne.nom + '.',
      );
    }
    const vendue = +ligne.q || 0;
    const montant = vendue > 0 ? auCentime(((+ligne.total || 0) * q) / vendue) : 0;
    lignes.push({
      id: uid(),
      ticket,
      ticketOrigineId: ligne.id,
      ticketOrigine: ligne.ticket || null,
      motif: motif || '',
      date: jour,
      clientId: ligne.clientId || null,
      ref: ligne.ref,
      nom: ligne.nom,
      u: ligne.u,
      q: -q,
      pv: ligne.pv,
      tx: ligne.tx != null ? +ligne.tx : null,
      total: -montant,
      pay: mode || 'especes',
    });
  }
  return lignes;
}

/**
 * Tickets de l'historique, une entrée par passage en caisse.
 *
 * <p>Une vente est enregistrée ligne par ligne ; l'historique les rassemble
 * sous leur numéro de ticket, le plus récent en tête, pour que l'on retrouve
 * ce que le client a en main.
 *
 * <p>Le montant à afficher est {@code ttc} : c'est le total que porte le
 * ticket remis au client, TVA comprise, calculé comme lui par taux. L'historique
 * montrait le hors-taxe, et le client qui comparait son ticket ne retrouvait
 * pas son montant.
 *
 * @param {Array} ventes lignes de vente, les plus récentes en tête
 * @param {number} [tauxDefaut] taux de la caisse, pour une ligne sans taux propre
 * @returns {Array<{ticket: string, lignes: Array, avoir: boolean, date: string,
 *           clientId: any, ht: number, ttc: number}>} les tickets
 */
export function ticketsDe(ventes, tauxDefaut) {
  const parTicket = new Map();
  for (const v of ventes || []) {
    const cle = v.ticket || 'L' + v.id;
    if (!parTicket.has(cle)) {
      parTicket.set(cle, {
        ticket: v.ticket || '',
        lignes: [],
        avoir: estAvoir(v),
        date: v.date,
        clientId: v.clientId,
        ht: 0,
      });
    }
    const t = parTicket.get(cle);
    t.lignes.push(v);
    t.ht = auCentime(t.ht + (+v.total || 0));
  }
  return [...parTicket.values()].map((t) => ({
    ...t,
    ttc: clotureCaisse(t.lignes, tauxDefaut).ttc,
  }));
}
